import { useEffect, useState } from 'react'
import { ApiError, api } from '../api/client.js'
import type { MappingResponse } from '../api/types.js'
import { Icon } from './icons.js'
import { Notice, Spinner } from './ui.js'

/**
 * Step one of an import: which column is what. Filled from the column names
 * where they are recognised; the AI button suggests a mapping for files that
 * look like nothing we know. Always editable by hand, and the block "An die
 * KI gesendet" shows exactly what would leave the server.
 */
export function ColumnMapper({
  csv,
  target,
  onChange,
}: {
  csv: string
  target: 'objects' | 'tasks'
  onChange: (mapping: Record<string, number>) => void
}) {
  const [data, setData] = useState<MappingResponse | null>(null)
  const [mapping, setMapping] = useState<Record<string, number>>({})
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [showSent, setShowSent] = useState(false)

  async function load(useAi: boolean) {
    setLoading(true)
    setError(null)
    try {
      const result = await api<MappingResponse>('/api/imports/mapping', { method: 'POST', body: { csv, target, useAi } })
      setData(result)
      setMapping(result.mapping)
      onChange(result.mapping)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Die Datei konnte nicht gelesen werden.')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (csv) void load(false)
  }, [csv]) // eslint-disable-line react-hooks/exhaustive-deps

  if (!csv) return null
  if (loading && !data) return <Spinner label="Spalten werden erkannt …" />
  if (error) return <Notice kind="error">{error}</Notice>
  if (!data) return null

  function set(field: string, value: string) {
    const next = { ...mapping }
    if (value === '') delete next[field]
    else next[field] = Number(value)
    setMapping(next)
    onChange(next)
  }

  const missingRequired = data.fields.filter((f) => f.required && mapping[f.key] === undefined)

  return (
    <div className="mapper">
      <div className="spread" style={{ marginBottom: 8 }}>
        <span className="strong">
          Spalten zuordnen <span className="muted small">({data.rows} Zeilen)</span>
        </span>
        <span className="actions">
          {data.source === 'ki' && (
            <span className="badge open">
              <Icon name="sparkle" size={12} /> KI-Vorschlag, bitte prüfen
            </span>
          )}
          <button type="button" className="btn secondary small" disabled={loading} onClick={() => void load(true)}>
            <Icon name="sparkle" size={15} /> {loading ? 'fragt …' : 'KI-Vorschlag'}
          </button>
        </span>
      </div>
      {data.aiMessage && <Notice kind="warn">{data.aiMessage}</Notice>}
      {missingRequired.length > 0 && <Notice kind="warn">Pflichtfeld ohne Spalte: {missingRequired.map((f) => f.label).join(', ')}</Notice>}
      <table className="data mapper-table">
        <tbody>
          {data.fields.map((field) => {
            const column = mapping[field.key]
            return (
              <tr key={field.key}>
                <td className="small">
                  {field.label}
                  {field.required ? ' *' : ''}
                </td>
                <td>
                  <select value={column ?? ''} onChange={(e) => set(field.key, e.target.value)}>
                    <option value="">nicht vorhanden</option>
                    {data.header.map((h, i) => (
                      <option key={i} value={i}>
                        {h || `Spalte ${i + 1}`}
                      </option>
                    ))}
                  </select>
                </td>
                <td className="muted small mapper-sample">{column !== undefined ? (data.sentToAi[0]?.[column] ?? '') : ''}</td>
              </tr>
            )
          })}
        </tbody>
      </table>
      <button type="button" className="linklike show-sent" onClick={() => setShowSent((v) => !v)}>
        {showSent ? 'Ausblenden' : 'Was würde an die KI gehen?'}
      </button>
      {showSent && (
        <div className="sent-preview">
          <div className="muted small">Nur beim Klick auf „KI-Vorschlag“ und nur, wenn der Assistent eingeschaltet ist:</div>
          <pre>{[data.header.join(' | '), ...data.sentToAi.map((row) => row.join(' | '))].join('\n')}</pre>
        </div>
      )}
    </div>
  )
}
